import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
    Dialog, Box, Typography, Button, IconButton,
    ToggleButton, ToggleButtonGroup, LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const BRAND = '#0d3b66';
const ACCENT = '#1976d2';

/**
 * Нативный полноэкранный показ презентации курса внутри SPA.
 * Слайды берутся из course.presentation.{en|es} (IndexedDB course store).
 *
 * Каждый слайд — это готовый HTML-«сцена» 1280×720 (slide.html), отрендеренный сервером
 * ТЕМ ЖЕ шаблоном, что и веб-страница участника, плюс общий CSS (presentation.css).
 * Поэтому вид в приложении 1-в-1 совпадает с веб-деском (лого, блоки, картинки, фон).
 * Здесь мы только вставляем HTML и масштабируем сцену под экран (как fitStage у участника).
 * Если html нет (старый кэш / pptx) — fallback на нативный MUI-рендер ниже.
 */
export default function SlideShowModal({ open, onClose, course, defaultLanguage = 'en' }) {
    const availableLangs = useMemo(() => (
        ['en', 'es'].filter(l => course?.presentation?.[l]?.source === 'local' && course.presentation[l].slides?.length)
    ), [course]);

    const [lang, setLang] = useState(defaultLanguage);
    const [idx, setIdx] = useState(0);

    const effectiveLang = availableLangs.includes(lang) ? lang : (availableLangs[0] || 'en');
    const pres = course?.presentation?.[effectiveLang] || null;

    const slides = useMemo(() => {
        const list = pres?.slides || [];
        return [...list].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
    }, [pres]);

    // Сброс на первый слайд при открытии/смене языка.
    useEffect(() => { if (open) setIdx(0); }, [open, effectiveLang]);

    const total = slides.length;
    const slide = slides[idx] || null;
    const isLast = idx >= total - 1;

    const goPrev = useCallback(() => setIdx(i => Math.max(0, i - 1)), []);
    const goNext = useCallback(() => setIdx(i => Math.min(total - 1, i + 1)), [total]);

    // Стрелки клавиатуры (внешняя клавиатура iPad / десктоп).
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'ArrowLeft') goPrev();
            if (e.key === 'ArrowRight') goNext();
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, goPrev, goNext, onClose]);

    // Масштабирование «сцены» 1280×720 целиком в доступный бокс + авто-подгон контента
    // под высоту страницы (shrink-to-fit) — точно как у веб-участника, ничего не обрезается.
    const stageWrapRef = useRef(null);
    const refit = useCallback(() => {
        const wrap = stageWrapRef.current;
        if (!wrap) return;
        const stage = wrap.querySelector('.tps-stage');
        if (!stage) return;

        // 1. Ужать контент в потоке под высоту страницы.
        const base = stage.querySelector('.tps-base');
        const fit = base && base.querySelector('.tps-fit');
        if (fit) {
            base.style.justifyContent = '';
            fit.style.transform = '';
            fit.style.transformOrigin = '';
            const cs = getComputedStyle(base);
            const avail = base.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
            const needed = fit.offsetHeight;
            if (needed > avail + 0.5 && needed > 0) {
                base.style.justifyContent = 'flex-start';
                fit.style.transformOrigin = 'top center';
                fit.style.transform = `scale(${avail / needed})`;
            }
        }

        // 2. Вписать всю сцену в доступный бокс.
        const availW = wrap.clientWidth;
        const availH = wrap.clientHeight;
        if (availW <= 0 || availH <= 0) return;
        const scale = Math.min(availW / 1280, availH / 720);
        stage.style.transform = `translate(-50%, -50%) scale(${scale})`;
    }, []);

    useEffect(() => {
        if (!open || !slide?.html) return undefined;
        // rAF — дать модалке (fullScreen Dialog) разложиться, чтобы контейнер получил размеры;
        // ResizeObserver добивает любые последующие изменения (анимация открытия, поворот экрана).
        const raf = requestAnimationFrame(refit);
        const wrap = stageWrapRef.current;
        let ro;
        if (wrap && typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => refit());
            ro.observe(wrap);
        }
        return () => { cancelAnimationFrame(raf); if (ro) ro.disconnect(); };
    }, [open, idx, effectiveLang, slide, refit]);

    if (!slide && open) {
        return null; // презентации нет — страховка (кнопка Present скрыта)
    }

    const light = slide?.bgText === 'light';
    const textColor = light ? 'rgba(255,255,255,0.95)' : '#34404f';
    const titleColor = light ? '#fff' : BRAND;

    return (
        <Dialog open={open} onClose={onClose} fullScreen
            PaperProps={{ sx: { backgroundColor: '#11161f', display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}>
            {/* Общий CSS «сцены» (один раз) — тот же, что у веб-участника. */}
            {pres?.css && <style dangerouslySetInnerHTML={{ __html: pres.css }} />}

            {/* Верхняя панель */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1, color: '#fff', flex: '0 0 auto',
            }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ flex: 1, opacity: 0.92 }}>
                    {pres?.title || course?.course || 'Presentation'}
                </Typography>
                {availableLangs.length > 1 && (
                    <ToggleButtonGroup
                        value={effectiveLang} exclusive size="small"
                        onChange={(_, v) => v && setLang(v)}
                        sx={{
                            '& .MuiToggleButton-root': { color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.25)', px: 1.5 },
                            '& .Mui-selected': { color: '#fff !important', backgroundColor: 'rgba(255,255,255,0.14) !important' },
                        }}
                    >
                        <ToggleButton value="en">EN</ToggleButton>
                        <ToggleButton value="es">ES</ToggleButton>
                    </ToggleButtonGroup>
                )}
                <Typography variant="body2" sx={{ opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>
                    {idx + 1} / {total}
                </Typography>
                <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
            </Box>

            {/* Слайд */}
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', px: { xs: 1.5, sm: 3 }, pb: 1 }}>
                {slide?.html ? (
                    <div
                        ref={stageWrapRef}
                        className="tps-stage-wrap"
                        data-fit="contain"
                        style={{ flex: 1, minHeight: 0, minWidth: 0, position: 'relative', overflow: 'hidden' }}
                        dangerouslySetInnerHTML={{ __html: slide.html }}
                    />
                ) : (
                    <Box sx={{
                        flex: 1, borderRadius: 3, overflow: 'auto', position: 'relative',
                        background: slide?.bgCss || '#fff',
                        display: 'flex', flexDirection: 'column', justifyContent: 'center',
                        p: { xs: 3, sm: 6 },
                    }}>
                        <SlideContent slide={slide} light={light} textColor={textColor} titleColor={titleColor} courseName={course?.course} />
                    </Box>
                )}
            </Box>

            {/* Навигация */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: { xs: 1.5, sm: 3 }, py: 1.5, flex: '0 0 auto' }}>
                <Button
                    variant="outlined" size="large" startIcon={<ArrowBackIcon />}
                    onClick={goPrev}
                    sx={{
                        minWidth: 130, color: '#fff', borderColor: 'rgba(255,255,255,0.35)',
                        visibility: idx > 0 ? 'visible' : 'hidden',
                        '&:hover': { borderColor: '#fff' },
                    }}
                >
                    Back
                </Button>
                <Box sx={{ flex: 1 }}>
                    <LinearProgress
                        variant="determinate"
                        value={total ? ((idx + 1) / total) * 100 : 0}
                        sx={{
                            height: 6, borderRadius: 3, backgroundColor: 'rgba(255,255,255,0.15)',
                            '& .MuiLinearProgress-bar': { borderRadius: 3, background: `linear-gradient(90deg, ${ACCENT}, #64b5f6)` },
                        }}
                    />
                </Box>
                <Button
                    variant="contained" size="large" endIcon={<ArrowForwardIcon />}
                    onClick={isLast ? onClose : goNext}
                    sx={{ minWidth: 130 }}
                >
                    {isLast ? 'Finish' : 'Next'}
                </Button>
            </Box>
        </Dialog>
    );
}

/* Fallback-рендер для старого кэша без slide.html (или pptx). Точная копия прежнего вида. */
function SlideContent({ slide, light, textColor, titleColor, courseName }) {
    if (!slide) return null;

    const titleChip = (
        <Box sx={{ width: 52, height: 4, borderRadius: 2, mb: 3, backgroundColor: light ? 'rgba(255,255,255,0.85)' : ACCENT }} />
    );
    const title = slide.title ? (
        <>
            <Typography sx={{ fontSize: { xs: 26, sm: 36 }, fontWeight: 800, lineHeight: 1.2, color: titleColor, mb: 1 }}>
                {slide.title}
            </Typography>
            {titleChip}
        </>
    ) : null;

    switch (slide.layout) {
        case 'cover':
            return (
                <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{
                        fontSize: 14, fontWeight: 800, letterSpacing: 4, textTransform: 'uppercase', mb: 2.5,
                        color: light ? 'rgba(255,255,255,0.85)' : ACCENT,
                    }}>
                        {courseName}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: 36, sm: 56 }, fontWeight: 800, lineHeight: 1.1, color: titleColor }}>
                        {slide.title}
                    </Typography>
                    {slide.secondaryBody && (
                        <>
                            <Box sx={{ width: 72, height: 4, borderRadius: 2, mx: 'auto', my: 3, backgroundColor: light ? 'rgba(255,255,255,0.7)' : ACCENT }} />
                            <Typography sx={{ fontSize: { xs: 17, sm: 22 }, lineHeight: 1.5, whiteSpace: 'pre-line', color: light ? 'rgba(255,255,255,0.95)' : '#5a6573', maxWidth: 760, mx: 'auto' }}>
                                {slide.secondaryBody}
                            </Typography>
                        </>
                    )}
                </Box>
            );

        case 'two_column':
            return (
                <Box>
                    {title}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2.5, sm: 5 } }}>
                        <Typography sx={{ flex: 1, fontSize: { xs: 16, sm: 19 }, lineHeight: 1.6, whiteSpace: 'pre-line', color: textColor }}>
                            {slide.body}
                        </Typography>
                        <Typography sx={{
                            flex: 1, fontSize: { xs: 16, sm: 19 }, lineHeight: 1.6, whiteSpace: 'pre-line', color: textColor,
                            borderLeft: { sm: `1px solid ${light ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.12)'}` },
                            pl: { sm: 5 },
                        }}>
                            {slide.secondaryBody}
                        </Typography>
                    </Box>
                </Box>
            );

        case 'quote':
            return (
                <Box sx={{ px: { xs: 1, sm: 6 } }}>
                    <Typography sx={{ fontSize: { xs: 22, sm: 30 }, fontStyle: 'italic', fontWeight: 500, lineHeight: 1.5, whiteSpace: 'pre-line', color: light ? '#fff' : '#1c2738' }}>
                        &ldquo;{slide.body}&rdquo;
                    </Typography>
                    {slide.attribution && (
                        <Typography sx={{ mt: 3, textAlign: 'right', fontWeight: 700, fontSize: { xs: 15, sm: 18 }, color: light ? 'rgba(255,255,255,0.9)' : BRAND }}>
                            — {slide.attribution}
                        </Typography>
                    )}
                </Box>
            );

        case 'image_text':
            return (
                <Box>
                    {title}
                    {slide.imageUrl && (
                        <Box component="img" src={slide.imageUrl} alt=""
                            sx={{ display: 'block', width: '100%', maxHeight: { xs: '42vh', sm: '52vh' }, objectFit: 'contain', mb: 2.5 }} />
                    )}
                    {slide.body && (
                        <Typography sx={{ fontSize: { xs: 16, sm: 19 }, lineHeight: 1.6, whiteSpace: 'pre-line', color: textColor }}>
                            {slide.body}
                        </Typography>
                    )}
                </Box>
            );

        case 'video': {
            const embed = slide.videoEmbed;
            return (
                <Box>
                    {title}
                    <Box sx={{ position: 'relative', width: '100%', pb: '56.25%', borderRadius: 2, overflow: 'hidden', backgroundColor: '#000', mb: slide.body ? 2.5 : 0 }}>
                        {slide.videoFileUrl ? (
                            <Box component="video" controls playsInline preload="metadata"
                                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                                <source src={slide.videoFileUrl} />
                            </Box>
                        ) : embed && (embed.kind === 'youtube' || embed.kind === 'vimeo') ? (
                            <Box component="iframe" src={embed.embedUrl} title="Video"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} />
                        ) : embed?.embedUrl ? (
                            <Box component="video" controls playsInline preload="metadata"
                                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                                <source src={embed.embedUrl} />
                            </Box>
                        ) : null}
                    </Box>
                    {slide.body && (
                        <Typography sx={{ fontSize: { xs: 16, sm: 19 }, lineHeight: 1.6, whiteSpace: 'pre-line', color: textColor }}>
                            {slide.body}
                        </Typography>
                    )}
                </Box>
            );
        }

        default: { // bullets
            const lines = (slide.body || '').split('\n').map(l => l.trim()).filter(Boolean);
            return (
                <Box>
                    {title}
                    {lines.map((line, i) => (
                        <Box key={i} sx={{ display: 'flex', mb: { xs: 2, sm: 2.5 } }}>
                            <Box sx={{
                                width: 11, height: 11, borderRadius: '50%', mt: { xs: 1, sm: 1.2 }, mr: 2.2, flexShrink: 0,
                                backgroundColor: light ? '#fff' : ACCENT,
                            }} />
                            <Typography sx={{ fontSize: { xs: 17, sm: 21 }, lineHeight: 1.5, color: textColor }}>
                                {line}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            );
        }
    }
}
